// @ts-nocheck
import { getStaffPermissions } from '@/lib/staffPermissions';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, ArrowLeft, CalendarDays, Loader2 } from 'lucide-react';
import StaffScoringView from '@/components/staff/tournaments/StaffScoringView';
import PeriodSettings from '@/components/staff/tournaments/PeriodSettings';
import BracketView from '@/components/staff/tournaments/BracketView';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';


interface StaffPermissions {
  canScore: boolean;
  canManageSchedule: boolean;
  canCheckIn: boolean;
  canView: boolean;
}

const ADMIN_ROLES = ['admin', 'org_admin', 'rlevents_super_admin', 'rlevents_tenant_admin'];
const SCHEDULE_FN = 'rle-public-schedule';
const NAVY = '#0a1f44';

const StaffTournamentPage: React.FC = () => {
  const { eventId } = useParams();
  const { profile, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<any>(null);
  const [games, setGames] = useState<any[]>([]);
  const [periodConfig, setPeriodConfig] = useState<any>({ period_type: 'none', period_count: 0 });
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<StaffPermissions>({
    canScore: false,
    canManageSchedule: false,
    canCheckIn: false,
    canView: false,
  });

  // --- Permission gate (operator/admin OR assigned staff) ---
  useEffect(() => {
    const checkPermissions = async () => {
      const userId = user?.id;
      if ((!userId && !profile?.email) || !eventId) return;

      let isOperator = false;

      if (userId) {
        const { data: roleRows } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);
        if ((roleRows || []).some((r: any) => ADMIN_ROLES.includes(r.role))) {
          isOperator = true;
        }

        if (!isOperator) {
          const { data: eventRow } = await supabase
            .from('events')
            .select('organization_id')
            .eq('id', eventId)
            .maybeSingle();
          const orgId = eventRow?.organization_id;
          if (orgId) {
            const { data: orgRow } = await supabase
              .from('organizations')
              .select('owner_id')
              .eq('id', orgId)
              .maybeSingle();
            if (orgRow?.owner_id === userId) {
              isOperator = true;
            } else {
              const { data: memberRow } = await supabase
                .from('organization_members')
                .select('id')
                .eq('organization_id', orgId)
                .eq('user_id', userId)
                .eq('status', 'active')
                .maybeSingle();
              if (memberRow) isOperator = true;
            }
          }
        }
      }

      if (isOperator) {
        setPermissions({
          canScore: true,
          canManageSchedule: true,
          canCheckIn: true,
          canView: true,
        });
        return;
      }

      // Assigned staff
      let staffRecord: any = null;
      if (userId) {
        const { data } = await supabase
          .from('event_staff')
          .select('id')
          .eq('user_id', userId)
          .eq('is_active', true)
          .maybeSingle();
        staffRecord = data;
      }
      if (!staffRecord && profile?.email) {
        const { data } = await supabase
          .from('event_staff')
          .select('id')
          .eq('email', profile.email)
          .eq('is_active', true)
          .maybeSingle();
        staffRecord = data;
      }

      if (!staffRecord) return;

      const { data: assignment } = await supabase
        .from('event_staff_assignments')
        .select('permissions, role')
        .eq('staff_id', staffRecord.id)
        .eq('event_id', eventId)
        .maybeSingle();

      if (assignment) {
        const { canScore, canCheckIn } = getStaffPermissions(assignment);
        setPermissions({
          canScore,
          canManageSchedule: false,
          canCheckIn,
          canView: true,
        });
      }
    };

    checkPermissions();
  }, [profile?.email, user?.id, eventId]);

  // --- Load the event's published schedule games (same source as public schedule / scoreboard) ---
  // silent = background re-read after a score saves: refresh the list without
  // flashing the full-page loader or wiping games already on screen.
  const loadGames = useCallback(async (silent = false) => {
    if (!eventId) return;
    if (!silent) {
      setLoading(true);
      setScheduleError(null);
    }


    // Resolve the event slug — the public schedule function is keyed by slug.
    const { data: rawEvent } = await supabase
      .from('events')
      .select('slug, name, sport_id, sports!sport_id(name)')
      .eq('id', eventId)
      .maybeSingle();
    const eventRow = rawEvent
      ? { slug: rawEvent.slug, name: rawEvent.name, sport: (rawEvent as any).sports?.name ?? undefined }
      : null;

    if (!eventRow?.slug) {
      if (!silent) {
        setScheduleError('NOT_FOUND');
        setLoading(false);
      }
      return;
    }


    const { data: res, error } = await supabaseClient.functions.invoke(SCHEDULE_FN, {
      body: { action: 'get', slug: eventRow.slug },
    });

    if (error) {
      if (!silent) setScheduleError('LOAD_ERROR');
    } else if (!res?.ok) {
      if (!silent) setScheduleError(res?.error?.code || 'LOAD_ERROR');
    } else {

      setEvent({ ...eventRow, ...(res.data?.event || {}) });
      const flat: any[] = [];
      (res.data?.days || []).forEach((day: any) => {
        (day.games || []).forEach((g: any) => flat.push(g));
      });

      // Load the event's period/scoring format config.
      const { data: cfg } = await supabase
        .from('event_format_config')
        .select('period_type, period_count')
        .eq('event_id', eventId)
        .maybeSingle();
      setPeriodConfig({
        period_type: cfg?.period_type || 'none',
        period_count: cfg?.period_count || 0,
      });

      // Load any existing per-period scores and attach to games.
      const gameIds = flat.map((g) => g.id).filter(Boolean);
      if (gameIds.length) {
        const { data: periodRows } = await supabaseClient
          .from('game_period_scores')
          .select('game_id, period_number, cum_a, cum_b, pts_a, pts_b')
          .in('game_id', gameIds);
        if (periodRows?.length) {
          const byGame: Record<string, any[]> = {};
          periodRows.forEach((p: any) => {
            (byGame[p.game_id] ??= []).push(p);
          });
          flat.forEach((g) => {
            g.periods = (byGame[g.id] || []).sort(
              (a, b) => a.period_number - b.period_number,
            );
          });
        }

        // Attach stored score-sheet image URLs.
        const { data: sheetRows } = await supabaseClient
          .from('scheduled_games')
          .select('id, score_sheet_image_url')
          .in('id', gameIds);
        if (sheetRows?.length) {
          const byId: Record<string, string | null> = {};
          sheetRows.forEach((r: any) => (byId[r.id] = r.score_sheet_image_url));
          flat.forEach((g) => {
            if (byId[g.id]) g.score_sheet_image_url = byId[g.id];
          });
        }
      }

      setGames(flat);
    }
    if (!silent) setLoading(false);

  }, [eventId]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  // Realtime: keep scores/status live across all scorekeepers.
  useEffect(() => {
    if (!games.length) return;
    const ids = new Set(games.map((g) => g.id));
    const channel = supabaseClient
      .channel(`staff-scoring-${eventId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'scheduled_games' },
        (payload: any) => {
          const row = payload.new;
          if (!row?.id || !ids.has(row.id)) return;
          setGames((prev) =>
            prev.map((g) =>
              g.id === row.id
                ? {
                    ...g,
                    status: row.game_status ?? g.status,
                    score_a: row.score_a ?? g.score_a,
                    score_b: row.score_b ?? g.score_b,
                    score_sheet_image_url:
                      row.score_sheet_image_url ?? g.score_sheet_image_url,
                  }
                : g,
            ),
          );
        },
      )
      .subscribe();
    return () => {
      supabaseClient.removeChannel(channel);
    };
  }, [eventId, games.length]);

  // Optimistic local update after a successful score call.
  const handleGameUpdated = (update: any) => {
    setGames((prev) =>
      prev.map((g) => (g.id === update.id ? { ...g, ...update } : g)),
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!permissions.canView) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
            <p className="text-muted-foreground">
              You don't have permission to score this event.
            </p>
            <Link to="/staff/dashboard">
              <Button className="mt-4">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Schedule not published yet.
  if (scheduleError === 'NOT_PUBLISHED') {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">Schedule not published</h3>
            <p className="text-muted-foreground">
              This event's schedule is not published yet. Publish it from Scheduling to start scoring.
            </p>
            <Link to="/staff/dashboard">
              <Button className="mt-4" variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (scheduleError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-destructive" />
            <h3 className="text-lg font-semibold mb-2">Could not load schedule</h3>
            <p className="text-muted-foreground">
              Something went wrong loading this event's games. Please try again.
            </p>
            <Button className="mt-4" variant="outline" onClick={() => loadGames()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex items-end justify-between gap-3">
        <div>
          <Link
            to="/staff/dashboard"
            className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Live Scoring
          </p>
          <h1 className="mt-1 text-2xl font-bold" style={{ color: NAVY }}>
            {event?.name}
          </h1>
        </div>
        {permissions.canManageSchedule && eventId && (
          <PeriodSettings
            eventId={eventId}
            periodType={periodConfig.period_type}
            periodCount={periodConfig.period_count}
            onSaved={setPeriodConfig}
          />
        )}
      </div>

      <Tabs defaultValue="games">
        <TabsList className="mb-4">
          <TabsTrigger value="games">Games</TabsTrigger>
          <TabsTrigger value="bracket">Bracket</TabsTrigger>
        </TabsList>
        <TabsContent value="games">
          {games.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CalendarDays className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No games scheduled</h3>
                <p className="text-muted-foreground">No games scheduled for this event.</p>
              </CardContent>
            </Card>
          ) : (
            <StaffScoringView
              games={games}
              canScore={permissions.canScore}
              periodConfig={periodConfig}
              sport={event?.sport}
              onGameUpdated={handleGameUpdated}
              onRefreshList={() => loadGames(true)}
            />

          )}
        </TabsContent>
        <TabsContent value="bracket">
          <BracketView eventId={eventId} canManage={permissions.canManageSchedule} />
        </TabsContent>
      </Tabs>

    </div>
  );
};

export default StaffTournamentPage;
